import throttle from '../base/_throttle.js';

export class AutoSizedText {
	static instance;

	static init( selector = '.auto-sized-text', opts = {} ) {
		if ( ! AutoSizedText.instance ) {
			AutoSizedText.instance = new AutoSizedText( selector, opts );
		} else {
			AutoSizedText.select( selector, opts );
		}

		return AutoSizedText.instance;
	}

	constructor( selector = '.auto-sized-text', opts = {} ) {
		this.selector = selector;
		this.opts     = opts;
		this.nodes    = [];

		const boundResize = this.resize.bind( this );
		this.observer = new MutationObserver( boundResize );

		this.select();
		this.resize();
		document.addEventListener( 'load', boundResize );
		window.addEventListener( 'resize', throttle( boundResize, 100 ) );
	}

	add( node, opts = {} ) {
		const textNode = new AutoSizedTextNode(
			node,
			{
				...this.opts,
				...opts,
			}
		);

		this.observer.observe(
			textNode.text,
			{
				childList: true,
				subtree: true,
				characterData: true,
			}
		);

		textNode.resize();
		this.nodes.push( textNode );
	}

	select( selector = this.selector, opts = {} ) {
		for ( const node of document.querySelectorAll( selector ) ) {
			this.add( node, opts );
		}
	}

	resize() {
		for ( const node of this.nodes ) {
			node.resize();
		}
	}
}

export class AutoSizedTextNode {
	constructor( node, opts ) {
		this.node = node;
		this.opts = {
			threshold: 2,
			maxAttempts: 500,
			minSize: parseInt( this.node?.dataset?.minSize ?? 12 ),
			maxSize: parseInt( this.node?.dataset?.maxSize ?? 512 ),
			...opts
		}

		if ( ! this.node ) {
			return;
		}

		this.threshold = 2;
		this.text = document.createElement( 'span' );
		this.text.classList.add( 'auto-sized-text__text' );
		this.text.style.setProperty( 'whiteSpace', 'nowrap', 'important' );
		this.text.style.setProperty( 'display', 'inline-block', 'important' );
		this.text.appendChild( document.createTextNode( this.node.textContent ) );

		while ( this.node.firstChild ) {
			this.node.removeChild( this.node.firstChild );
		}

		this.node.appendChild( this.text );
		this.resize();
	}

	resize() {
		const upper       = this.node.offsetWidth;
		const lower       = this.node.offsetWidth - this.threshold;
		const maxAttempts = Math.min( this.opts.maxAttempts, 1000 );
		let   attempts    = 0;

		while ( attempts <= maxAttempts ) {
			const { scrollWidth } = this.text;

			if ( scrollWidth >= lower && scrollWidth <= upper ) {
				break;
			}

			const fontSize = parseFloat( getComputedStyle( this.text ).fontSize );

			if ( scrollWidth < lower ) {
				if ( fontSize >= this.opts.maxSize ) {
					this.applySize( this.opts.maxSize );
					break;
				}

				this.applySize( fontSize + 1 );
			} else {
				if ( fontSize <= this.opts.minSize ) {
					this.applySize( this.opts.minSize, 'wrap' );
					break;
				}

				this.applySize( fontSize - 1 );
			}

			attempts++;
		}
	}

	applySize( size, wrap = 'nowrap' ) {
		this.text.style.setProperty( 'display', 'inline-block', 'important' );
		this.text.style.setProperty( 'font-size', `${ size }px`, 'important' );

		if ( wrap ) {
			this.text.style.setProperty( 'white-space', wrap, 'important' );
		}
	}
}

if ( [ 'complete', 'interactive' ].includes( document.readyState ) ) {
	AutoSizedText.init();
} else {
	document.addEventListener( 'DOMContentLoaded', () => AutoSizedText.init() );
}

export default AutoSizedText.instance;
